import { faLink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Props, State } from "./interfaces";
import { AddButton, DialogContent } from "./styles";
import AddEditTransaction from "../../../../../../containers/AddTransaction/AddEditTransaction/AddEditTransaction";
import DialogActions from "../../../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../../../containers/LoadingOverlay/LoadingOverlay";
import { PropsFilter } from "../../../../../../models/api/props-filter";
import { BankTransactionDTO } from "../../../../../../models/bank-transaction";
import { CashTransactionDTO } from "../../../../../../models/cash-transaction";
import { CustomDate } from "../../../../../../models/custom-date/custom-date";
import { TransactionDTO } from "../../../../../../models/transactions";
import Table from "../../../../../../reusable-components/Table/Table";
import { saveBankTransaction } from "../../../../../../services/bank-transaction/bank-transaction-api.service";
import { saveCashTransaction } from "../../../../../../services/cash-transaction/cash-transaction-api.service";
import { TransactionsApiService } from "../../../../../../services/transactions/transactions-api.service";
import { FT_ABATT } from "../../../../../../utils/constants/filter-tables-constants";
import { convertDateToUTC, intlFormatDateShort } from "../../../../../../utils/time-utils";

export const AddBookedAcountingTransactions = ({ item, handleNotifyParent, isBankTransaction }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.link_amount";
  const translationFields = `${tRP}.fields`;

  const checkedTransactions = useRef([]);

  const DEFAULT_CURR_ITEM = useMemo(() => {
    return {};
  }, [item]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      error: "",
      isBankTransaction,
      currItem: item ?? DEFAULT_CURR_ITEM,
      isTableMounted: false,
    };
  }, [DEFAULT_CURR_ITEM, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const transactionIds = useMemo(() => {
    return state.currItem?.transactions.map((transaction) => transaction.id) ?? [];
  }, [state.currItem?.transactions]);

  const date = useMemo(() => {
    const thisDate = new Date(state.currItem.date as Date);
    return thisDate;
  }, [state.currItem?.date]);

  const handleSaveBookedAmount = useCallback(
    async (transaction: TransactionDTO[] | TransactionDTO) => {
      setState((state) => ({ ...state, loading: true, isTableMounted: false }));

      const newTransactions = Array.isArray(transaction) ? transaction : [transaction];

      try {
        const response = state.isBankTransaction
          ? await saveBankTransaction({
              ...(state.currItem as BankTransactionDTO),
              date: state.currItem.date ? convertDateToUTC(state.currItem.date) : null,
              transactions: [...(state.currItem?.transactions ?? []), ...newTransactions],
            })
          : await saveCashTransaction({
              ...(state.currItem as CashTransactionDTO),
              date: state.currItem.date ? convertDateToUTC(state.currItem.date) : null,
              transactions: [...(state.currItem?.transactions ?? []), ...newTransactions],
            });

        if (!response.error) {
          setState({ ...INITIAL_STATE });

          handleNotifyParent();
        }
      } catch (error) {
        setState((state) => ({
          ...state,
          loading: false,
          isTableMounted: true,
        }));
      }
    },
    [INITIAL_STATE, handleNotifyParent, state.currItem]
  );

  const handleClose = useCallback(() => {
    checkedTransactions.current = [];

    setState({ ...INITIAL_STATE });
  }, [INITIAL_STATE]);

  useEffect(() => {
    !!item &&
      setState((state) => ({
        ...state,
        currItem: item,
      }));

    return () => {
      checkedTransactions.current = [];

      setState({ ...INITIAL_STATE });
    };
  }, [item, INITIAL_STATE]);

  const columns: GridColumns = [
    {
      field: "type",
      valueGetter: ({ row }) => row?.type?.name,
      headerName: t(`${translationFields}.type`),
      headerAlign: "center",
      align: "left",
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "transactionDate",
      valueGetter: ({ row }) => intlFormatDateShort(CustomDate.ToDate(row.transactionDate) as Date),
      headerName: t(`${translationFields}.date`),
      headerAlign: "center",
      align: "center",
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "client",
      valueGetter: ({ row }) => row?.client?.name,
      headerName: t(`${translationFields}.client`),
      headerAlign: "center",
      align: "left",
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "vendor",
      valueGetter: ({ row }) => row?.vendor?.name,
      headerName: t(`${translationFields}.vendor`),
      headerAlign: "center",
      align: "left",
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "amount",
      valueGetter: ({ row }) =>
        row?.amount?.toLocaleString("it-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      headerName: t(`${translationFields}.amount`),
      headerAlign: "center",
      align: "right",
      flex: 1,
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const defaultItem: Partial<TransactionDTO> = {
    type: null,
    transactionDate: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      day: new Date().getDate(),
    },
    amount: 0,
  };

  return (
    <>
      <AddButton
        onClick={() => {
          setState((state) => ({
            ...state,
            isDialogOpen: true,
            isTableMounted: true,
          }));
        }}
        // variant={"outlined"}
        plusButton={true}
        title={"Link existing transactions"}
      >
        <FontAwesomeIcon icon={faLink} />
      </AddButton>

      <Dialog
        open={state.isDialogOpen}
        onClose={handleClose}
        fullWidth
        maxWidth={"lg"}
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={"Link existing transaction"}
            handleClose={handleClose}
          />
          <DialogContent>
            <Table
              tableHeight={500}
              translationRootPath={tRP}
              tableHeightAdjustment={120}
              columns={columns}
              displayTitle={false}
              serviceApi={new TransactionsApiService()}
              defaultItem={defaultItem}
              filter={FT_ABATT}
              showNoItems={!state.isTableMounted}
              addEditType="custom"
              CustomAddEditComponent={AddEditTransaction}
              propsForCustomAddEdit={{
                typeOfTransactions: "bank-and-cash",
              }}
              propsFilter={
                {
                  notIds: transactionIds,
                  date: convertDateToUTC(date),
                  legalEntityIds: [state.currItem.legalEntity?.id],
                } as PropsFilter
              }
              checkedRows={checkedTransactions}
              CustomButtonToRender={() => {
                return <></>;
              }}
            />
          </DialogContent>
          <DialogActions
            handleCancel={handleClose}
            handleSave={() => handleSaveBookedAmount(checkedTransactions.current)}
            customSaveName={"Link"}
            loading={state.loading}
            error={state.error}
          />
        </LoadingOverlay>
      </Dialog>
    </>
  );
};
